export class AssertionError extends Error {}

export type FnType<Params extends unknown[] = unknown[], Result = unknown> = (
  ...args: Params
) => Result;

export function isDefined<T>(x: T): x is NonNullable<T> {
  return x !== null && x !== undefined;
}

export function assertDefined<T>(
  x: T,
  msg: string,
): asserts x is NonNullable<T> {
  if (x === null || x === undefined) {
    throw new AssertionError(msg || `Failed assertion, ${x} should be defined`);
  }
}

export function assertedDefined<T>(x: T, msg: string): NonNullable<T> {
  assertDefined(x, msg);
  return x;
}

// In place where you don't want to make an if-else branch depending on the type guard because failing the guard would only mean there's a bug,
//  you can use this instead to throw when the type guard doesn't check
export function assertKind<P, T extends P>(
  x: P,
  fn: (a: P) => a is T,
  msg?: string,
): asserts x is T {
  if (!fn(x)) {
    throw new AssertionError(msg ?? `${x} is the wrong type. This is a bug.`);
  }
}

/** Asserts the type of the input with a type guard predicate, then returns the narrowed type. Throws is the type doesn't check out. */
export function assertedCast<P, T extends P>(
  x: P,
  fn: (a: P) => a is T,
  msg?: string,
): T {
  if (!fn(x)) {
    throw new AssertionError(msg ?? `${x} is the wrong type. This is a bug.`);
  }
  return x;
}

export const reverseTypeGuard =
  <I, O extends I>(f: (x: I) => x is O) =>
  (x: I): x is Exclude<I, O> =>
    !f(x);

export function isDefinedWithFields<T extends { [key: string]: unknown }>(
  x: T | undefined | null,
): x is DefinedFields<T> {
  return isDefined(x) && Object.values(x).every(isDefined);
}

export type Defined<T> = DefinedFields<T>;
export type NonOptional<T> = Required<T>;
export type RequiredNonNullable<T> = {
  [K in keyof T]-?: NonNullable<T[K]>;
};
export type DefinedFields<T, F extends keyof T = keyof T> = {
  [K in F]-?: NonNullable<T[K]>;
} & Pick<T, Exclude<keyof T, F>>;

export type DropUndefined<T> = {
  [K in keyof T]: T[K] extends undefined ? never : T[K];
};

/** Pick given fields but also enforce that no other fields are set */
export type StrictPick<T, K extends keyof T> = {
  [U in K]: T[U];
} & {
  [U in Exclude<keyof T, K>]?: undefined;
};

export function exhaustiveCheck(x: never, msg?: string): never {
  throw new AssertionError(msg || `Unexpected type value ${x}`);
}

export type WithId<T extends Record<string, unknown>> = T & {
  id: number;
};

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Returns the keys of an object and casts the type accurately. Use instead of Object.keys to avoid accidental wrong type casts.
 * The return types match exactly the actual types that Object.keys returns.
 * Examples:
 * typeof keys({ a: 1, b: 2 }) === ('a' | 'b')[];
 * typeof keys({ a: 1, 13: 4 }) === ('a' | '13')[];
 * typeof keys({ a: 1, [Symbol('b')]: 2 }) === 'a'[];
 */
export function keys<T extends object>(
  obj: T,
): `${keyof T & (string | number)}`[] {
  return Object.keys(obj) as `${keyof T & (string | number)}`[];
}

export type NumericKeys<T> = {
  [K in keyof T]: T[K] extends number | undefined | null ? K : never;
}[keyof T];